/**
 * Builds a deep link into the recipient's webmail, filtered to our sender address, for the
 * "Verify your email" button on the check-your-email screen.
 *
 * Replaces the abandoned `sniper-link` package (last published 2022). URL templates are copied
 * from it verbatim so links behave identically. Two deliberate differences:
 *
 *  - sniper-link's default export silently discarded the `daysAgo` argument this app was passing,
 *    so no date filter was ever applied. That behaviour is preserved rather than "fixed", to keep
 *    the generated links the same.
 *  - sniper-link threw when handed an empty address, which would crash this screen on a direct
 *    navigation. Here that returns a null link instead, which the caller already renders a
 *    fallback for.
 */

export type InboxProvider = 'google' | 'yahoo' | 'microsoft' | 'proton' | 'icloud'

const TEMPLATES: Record<InboxProvider, string> = {
	google:    'https://mail.google.com/mail/u/{USER_EMAIL}/#search/from%3A({FROM_EMAIL})+in%3Aanywhere',
	microsoft: 'https://outlook.live.com/mail/?login_hint={USER_EMAIL}',
	yahoo:     'https://mail.yahoo.com/d/search/keyword=from%253A{FROM_EMAIL}',
	proton:    'https://mail.proton.me/u/0/all-mail#from={FROM_EMAIL}',
	icloud:    'https://www.icloud.com/mail/',
}

const detectProvider = (email: string): InboxProvider | null => {
	if (/@(gmail|googlemail|google)\.com/.test(email)) return 'google'
	if (/@yahoo\.(com|co\.uk|fr|it)/.test(email) || /@(ymail|rocketmail)\.com/.test(email)) return 'yahoo'
	if (/@(outlook|live|hotmail|msn|passport)\.com/.test(email) || email.includes('@passport.net')) return 'microsoft'
	if (email.includes('@proton.me') || email.includes('@protonmail.com')) return 'proton'
	if (email.includes('@icloud.com')) return 'icloud'
	return null
}

export const buildInboxLink = (
	{ email, from }: { email?: string | null, from: string },
): { provider: InboxProvider | null, link: string | null } => {
	if (!email) return { provider: null, link: null }

	const provider = detectProvider(email)
	if (!provider) return { provider: null, link: null }

	const link = TEMPLATES[provider]
		.replace('{USER_EMAIL}', encodeURIComponent(email))
		.replace('{FROM_EMAIL}', encodeURIComponent(from))

	return { provider, link }
}
